import React, { useState, useEffect, useRef } from 'react';
import styled, { css } from 'styled-components';
import {
  MapCategory,
  MapProgressStatusList,
} from '../../../../interface/aiAnalyze/aiRecommend/data';
import SpecialConditionOption from './SpecialConditionModal';
import { useMapContext } from '../../../../context/MapContext';
import BottomModal from '../../../components/modal/BottomModal';
import { formatToKoreanCurrency } from '../../../../func/func';
import CalendarModal from './CalendarModal';

interface FilterModalProps {
  visible: boolean;
  onClose: () => void;
}

const FilterModal = ({ visible, onClose }: FilterModalProps) => {
  const { mapFilter, setMapFilter } = useMapContext();

  const TABS = [
    { label: '전체', value: '' },
    { label: '경매', value: 'A' },
    { label: '공매', value: 'P' },
    { label: '예정', value: 'E' },
    { label: 'NPL', value: 'N' },
    { label: '실거래', value: 'S' },
  ];
  const TAB_MAPPING: Record<string, string> = {
    전체: '',
    경매: 'A',
    공매: 'P',
    예정: 'E',
    NPL: 'N',
    실거래: 'S',
  };
  const formatMan = (p: string) => {
    if (!p) return;
    const np = Number(p);

    return String(np / 10000);
  };

  const [openSpecialConditionModal, setOpenSpecialConditionModal] =
    useState(false);

  const [openDatePicker, setOpenDatePicker] = useState(false);
  const [openDatePickerEnd, setOpenDatePickerEnd] = useState(false);
  const dateRef = useRef<HTMLDivElement>(null);
  const dateEndRef = useRef<HTMLDivElement>(null);

  const [minPriceInput, setMinPriceInput] = useState(
    formatMan(mapFilter.minPrice),
  );
  const [maxPriceInput, setMaxPriceInput] = useState(
    formatMan(mapFilter.maxPrice),
  );
  const [minBidCountInput, setMinBidCountInput] = useState(
    mapFilter.minBidCount,
  );
  const [maxBidCountInput, setMaxBidCountInput] = useState(
    mapFilter.maxBidCount,
  );

  const handleMinBlur = () => {
    if (!minPriceInput) return;
    setMapFilter((prev) => ({
      ...prev,
      minPrice: String(Number(minPriceInput) * 10000),
    }));
  };

  const handleMaxBlur = () => {
    if (!maxPriceInput) return;
    setMapFilter((prev) => ({
      ...prev,
      maxPrice: String(Number(maxPriceInput) * 10000),
    }));
  };
  const handleMinBidBlur = () => {
    if (!minBidCountInput) return;
    setMapFilter((prev) => ({ ...prev, minBidCount: minBidCountInput }));
  };

  const handleMaxBidBlur = () => {
    if (!maxBidCountInput) return;
    setMapFilter((prev) => ({ ...prev, maxBidCount: maxBidCountInput }));
  };

  const handleClickChip = (item: string) => {
    setMapFilter((prev) => {
      return {
        ...prev,
        specialItemCondition: prev.specialItemCondition.filter(
          (v) => v !== item,
        ),
      };
    });
  };

  const toggleCategory = (value: string) => {
    if (value === '') {
      setMapFilter({
        ...mapFilter,
        multiItemStatus: [],
      });
    } else {
      setMapFilter((prev: any) => {
        const has = prev.multiItemStatus.includes(value);
        return {
          ...prev,
          multiItemStatus: has
            ? prev.multiItemStatus.filter((v: any) => v !== value) // 제거
            : [...prev.multiItemStatus, value], // 추가
        };
      });
    }
  };

  const toggleProgress = (value: string) => {
    if (value === '') {
      setMapFilter({
        ...mapFilter,
        progressStatus: [],
      });
    } else {
      setMapFilter((prev: any) => {
        const has = prev.progressStatus.includes(value);
        return {
          ...prev,
          progressStatus: has
            ? prev.progressStatus.filter((v: any) => v !== value) // 제거
            : [...prev.progressStatus, value], // 추가
        };
      });
    }
  };

  const handleClickReset = () => {
    setMinPriceInput('');
    setMaxPriceInput('');
    setMinBidCountInput('');
    setMaxBidCountInput('');

    setMapFilter({
      ...mapFilter,
      multiItemStatus: [],
      progressStatus: ['A', 'C'],
      specialItemCondition: [],
      specialItemCond: 'A',
      minPrice: '',
      maxPrice: '',
      minSaleDate: '',
      maxSaleDate: '',
      minBidCount: '',
      maxBidCount: '',
    });
  };

  const toggleTab = (tabName: string) => {
    if (tabName === '전체') {
      // 전체는 단독 선택
      setMapFilter({ ...mapFilter, type: '' });
    } else {
      const code = TAB_MAPPING[tabName];
      const currentTabs = mapFilter.type ? mapFilter.type.split(',') : [];

      const hasTab = currentTabs.includes(code);
      let newTabs: string[];

      if (hasTab) {
        // 이미 선택된 탭이면 제거
        newTabs = currentTabs.filter((t) => t !== code);
      } else {
        // 새로 선택된 탭 추가
        newTabs = [...currentTabs, code];
      }

      // 탭이 하나도 선택되지 않으면 '전체'로 간주
      setMapFilter({
        ...mapFilter,
        type: newTabs.length === 0 ? '' : newTabs.join(','),
      });
    }
  };

  if (!visible) return null;

  return (
    <BottomModal onClose={onClose}>
      <Container>
        <Header>
          <Title>필터</Title>
          <CloseButton
            src="/assets/images/close/close-black.svg"
            alt="close"
            onClick={onClose}
          />
        </Header>
        <Inner>
          <Section>
            <SectionTitle>마커</SectionTitle>
            <ChipContainer>
              {TABS.map(({ label, value }) => {
                const isActive = () => {
                  const type = mapFilter.type;
                  if (label === '전체') return type === '';
                  return type.split(',').includes(TAB_MAPPING[label]);
                };
                return (
                  <OptionChip
                    key={label}
                    $selected={isActive()}
                    onClick={() => {
                      toggleTab(label);
                    }}
                  >
                    {label}
                  </OptionChip>
                );
              })}
            </ChipContainer>
          </Section>

          <Section>
            <SectionTitle>물건 종류</SectionTitle>
            <ChipContainer>
              {MapCategory.map((option) => {
                const isActive = () => {
                  if (option.label === '전체') {
                    return mapFilter.multiItemStatus.length === 0;
                  } else {
                    return mapFilter.multiItemStatus.includes(option.value);
                  }
                };
                return (
                  <OptionChip
                    key={option.value}
                    $selected={isActive()}
                    onClick={() => toggleCategory(option.value)}
                  >
                    {option.label}
                  </OptionChip>
                );
              })}
            </ChipContainer>
          </Section>

          <Section>
            <SectionTitle>진행단계</SectionTitle>
            <ChipContainer>
              {MapProgressStatusList.map((status) => {
                const isActive = () => {
                  if (status.label === '전체') {
                    return mapFilter.progressStatus.length === 0;
                  } else {
                    return mapFilter.progressStatus.includes(status.value);
                  }
                };
                return (
                  <Status
                    key={status.value}
                    $selected={isActive()}
                    onClick={() => toggleProgress(status.value)}
                  >
                    {status.label}
                  </Status>
                );
              })}
            </ChipContainer>
          </Section>

          <Section>
            <SectionTitle>가격</SectionTitle>
            <InputSection>
              <InputContainer>
                <InputBox>
                  <Input
                    value={minPriceInput}
                    placeholder="최소"
                    onChange={(e) => {
                      const onlyNums = e.target.value.replace(/[^0-9]/g, ''); // 숫자 외 제거
                      setMinPriceInput(onlyNums);
                    }}
                    onBlur={handleMinBlur}
                    inputMode="numeric"
                    pattern="[0-9]*"
                  />
                  <span>만원</span>
                </InputBox>
                {mapFilter.minPrice && (
                  <InputDesc>
                    {formatToKoreanCurrency(mapFilter.minPrice)} 이상
                  </InputDesc>
                )}
              </InputContainer>
              <InputDivider>~</InputDivider>
              <InputContainer>
                <InputBox>
                  <Input
                    value={maxPriceInput}
                    placeholder="최대"
                    onChange={(e) => {
                      const onlyNums = e.target.value.replace(/[^0-9]/g, ''); // 숫자 외 제거
                      setMaxPriceInput(onlyNums);
                    }}
                    onBlur={handleMaxBlur}
                    inputMode="numeric"
                    pattern="[0-9]*"
                  />
                  <span>만원</span>
                </InputBox>
                {mapFilter.maxPrice && (
                  <InputDesc>
                    {formatToKoreanCurrency(mapFilter.maxPrice)} 이하
                  </InputDesc>
                )}
              </InputContainer>
            </InputSection>
          </Section>

          <Section>
            <SectionTitle>매각기일</SectionTitle>
            <InputSection>
              <InputContainer ref={dateRef}>
                <DateInput onClick={() => setOpenDatePicker(true)}>
                  <DateValue $active={!!mapFilter.minSaleDate}>
                    {mapFilter.minSaleDate
                      ? mapFilter.minSaleDate
                      : '연도.월.일.'}
                  </DateValue>
                </DateInput>
              </InputContainer>
              <InputDivider>~</InputDivider>
              <InputContainer ref={dateEndRef}>
                <DateInput onClick={() => setOpenDatePickerEnd(true)}>
                  <DateValue $active={!!mapFilter.maxSaleDate}>
                    {mapFilter.maxSaleDate
                      ? mapFilter.maxSaleDate
                      : '연도.월.일.'}
                  </DateValue>
                </DateInput>
              </InputContainer>
            </InputSection>
          </Section>

          <Section>
            <SectionTitle>유찰횟수</SectionTitle>
            <InputSection>
              <InputContainer>
                <InputBox>
                  <Input
                    value={minBidCountInput}
                    placeholder="횟수"
                    onChange={(e) => {
                      const onlyNums = e.target.value.replace(/[^0-9]/g, ''); // 숫자 외 제거
                      setMinBidCountInput(onlyNums);
                    }}
                    onBlur={handleMinBidBlur}
                    inputMode="numeric"
                    pattern="[0-9]*"
                  />
                  <span>이상</span>
                </InputBox>
              </InputContainer>
              <InputDivider>~</InputDivider>
              <InputContainer>
                <InputBox>
                  <Input
                    value={maxBidCountInput}
                    placeholder="횟수"
                    onChange={(e) => {
                      const onlyNums = e.target.value.replace(/[^0-9]/g, ''); // 숫자 외 제거
                      setMaxBidCountInput(onlyNums);
                    }}
                    onBlur={handleMaxBidBlur}
                    inputMode="numeric"
                    pattern="[0-9]*"
                  />
                  <span>이하</span>
                </InputBox>
              </InputContainer>
            </InputSection>
          </Section>

          <Section onClick={() => setOpenSpecialConditionModal(true)}>
            <SectionTitle>특수조건 {'>'}</SectionTitle>
            {mapFilter.specialItemCondition.length > 0 ? (
              <ChipContainer>
                {mapFilter.specialItemCondition.map((item) => (
                  <Chip
                    key={item}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleClickChip(item);
                    }}
                  >
                    {item}
                    <img src="/assets/images/close/close-gray.svg" alt="" />
                  </Chip>
                ))}
              </ChipContainer>
            ) : (
              <EmptyText>특수조건이 설정되어있지 않습니다.</EmptyText>
            )}
          </Section>
        </Inner>
        <ButtonGroup>
          <ResetButton
            onClick={() => {
              handleClickReset();
            }}
          >
            초기화
          </ResetButton>
          <ApplyButton
            onClick={() => {
              onClose();
            }}
          >
            적용
          </ApplyButton>
        </ButtonGroup>
        {openDatePicker && (
          <CalendarModal
            date={mapFilter.minSaleDate}
            maxDate={mapFilter.maxSaleDate ?? ''}
            setDate={(s: string) => {
              setMapFilter({
                ...mapFilter,
                minSaleDate: s,
              });
            }}
            onClose={() => setOpenDatePicker(false)}
          />
        )}
        {openDatePickerEnd && (
          <CalendarModal
            date={mapFilter.maxSaleDate}
            minDate={mapFilter.minSaleDate ?? ''}
            setDate={(s: string) => {
              setMapFilter({
                ...mapFilter,
                maxSaleDate: s,
              });
            }}
            onClose={() => setOpenDatePickerEnd(false)}
          />
        )}
        {openSpecialConditionModal && (
          <SpecialConditionOption
            viewOption={mapFilter.specialItemCond}
            options={mapFilter.specialItemCondition}
            onConfirm={(o: string[], v: string) =>
              setMapFilter({
                ...mapFilter,
                specialItemCondition: o,
                specialItemCond: v,
              })
            }
            onClose={() => setOpenSpecialConditionModal(false)}
          />
        )}
      </Container>
    </BottomModal>
  );
};

export default FilterModal;

const Container = styled.div`
  display: flex;
  height: 640px;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
  align-self: stretch;
  width: 100%;
`;

const Header = styled.div`
  display: flex;
  padding: 16px;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
  position: relative;
`;
const Title = styled.div`
  flex: 1 0 0;
  color: #222;
  text-align: center;
  font-size: 18px;
  font-weight: 700;
  line-height: 145%;
  letter-spacing: -0.36px;
`;
const CloseButton = styled.img`
  position: absolute;
  right: 16px;
  top: 17px;
  cursor: pointer;
`;

const Inner = styled.div`
  display: flex;
  padding: 16px;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  flex: 1 0 0;
  align-self: stretch;
  width: 100%;
  overflow-y: auto;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
  width: 100%;
`;
const SectionTitle = styled.div`
  color: #222;
  font-size: 14px;
  font-weight: 500;
  line-height: 145%;
  letter-spacing: -0.28px;
`;

const ChipContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
  flex-wrap: wrap;
`;

const OptionChip = styled.div<{ $selected: boolean }>`
  display: flex;
  padding: 6px 12px;
  flex-direction: column;
  align-items: center;
  border-radius: 20px;
  border: 1px solid #e1e2e4;
  background: #fff;
  cursor: pointer;

  color: #616161;
  text-align: center;
  font-size: 12px;
  font-weight: 500;
  line-height: 145%;
  letter-spacing: -0.24px;

  ${({ $selected }) =>
    $selected &&
    css`
      border-color: #008aff;
      color: #008aff;
    `}
`;

const Status = styled(OptionChip)`
  padding: 8px 12px;
  flex: 1 0 0;
  border-radius: 8px;
`;

const InputSection = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: flex-start;
  gap: 4px;

  align-self: stretch;
  color: #616161;
  font-size: 14px;
  font-weight: 500;
  line-height: 145%;
  letter-spacing: -0.28px;
`;

const InputDivider = styled.div`
  flex: 0 0 auto;
  white-space: nowrap;
  display: flex;
  width: 8px;
  height: 40px;
  flex-direction: column;
  justify-content: center;
`;

const InputContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
  flex: 1 1 0;
  min-width: 0;
`;
const InputBox = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  padding: 10px 12px;
  border-radius: 8px;
  background: #f7f8fc;
  width: 100%;
  min-width: 0;
  & > span {
    white-space: nowrap;
  }
`;

const Input = styled.input`
  outline: none;
  border: none;
  background-color: transparent;
  flex: 1 1 0;
  min-width: 0;
  color: #222;
  font-size: 16px;
  font-weight: 500;
  line-height: 145%;
  letter-spacing: -0.28px;

  &::placeholder {
    color: #37383c9c;
  }
`;

const InputDesc = styled.div`
  display: flex;
  padding: 0 12px;
  align-items: flex-start;
  gap: 10px;
  align-self: stretch;
`;

const DateInput = styled.div<{ $active?: boolean }>`
  display: flex;
  padding: 10px 12px;
  justify-content: flex-end;
  gap: 4px;
  border-radius: 8px;
  background: #f7f8fc;
  cursor: pointer;
  border: 1px solid transparent;
  ${({ $active }) => $active && 'border-color:#008AFF;'}
`;

const DateValue = styled.div<{ $active?: boolean }>`
  flex: 1 0 0;
  color: ${({ $active }) => ($active ? '#222' : 'rgba(55, 56, 60, 0.61)')};
  font-size: 16px;
  font-weight: 500;
  line-height: 145%;
  letter-spacing: -0.28px;
`;

const Chip = styled.div`
  display: flex;
  padding: 6px 12px;
  align-items: center;
  gap: 4px;
  border-radius: 9999px;
  background: #f7f8fc;

  color: #222;
  font-size: 12px;
  font-weight: 500;
  line-height: 145%;
  letter-spacing: -0.24px;
`;

const EmptyText = styled.div`
  color: #616161;
  font-size: 14px;
  font-weight: 400;
  line-height: 145%;
  letter-spacing: -0.28px;
`;

const ButtonGroup = styled.div`
  padding: 16px;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
`;

const Button = styled.button`
  border: none;
  outline: none;

  display: flex;
  padding: 12px 28px;
  justify-content: center;
  align-items: center;
  border-radius: 8px;

  text-align: center;
  font-size: 16px;
  font-weight: 500;
  line-height: 145%;
  letter-spacing: -0.32px;
`;

const ResetButton = styled(Button)`
  color: #616161;
  background: rgba(112, 115, 124, 0.08);
`;

const ApplyButton = styled(Button)`
  flex: 1 0 0;
  background: #008aff;
  color: #fff;
`;
